// Browser Sentry wiring. init() runs once from main.tsx before React mounts;
// the identity helpers are driven by <SentryIdentity> as Clerk auth resolves.
// All config is build-time inlined from VITE_* env (see release.yml SSM load).
import * as Sentry from "@sentry/react";

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  // No DSN (local dev / preview) → Sentry stays uninitialised and every
  // Sentry.* call below is a safe no-op.
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
      import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    // Error reporting only for now. Raise to enable browser performance tracing.
    tracesSampleRate: 0,
  });
}

// Tags every subsequent event with the signed-in identity. org_id mirrors the
// platform/worker tag so an error can be pivoted across all three Sentry
// projects by the same org.
export function setSentryUser(userId: string, orgId: string | null): void {
  Sentry.setUser({ id: userId });
  Sentry.getCurrentScope().setTag("org_id", orgId ?? undefined);
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.getCurrentScope().setTag("org_id", undefined);
}

// Correlates a browser API call with its backend request: the same id rides the
// x-request-id header to the platform (which tags its own Sentry events with
// it) and back onto any ApiError / breadcrumb raised here.
export function newRequestId(): string {
  return crypto.randomUUID();
}
